import { call, fork, put, select, takeLatest } from 'redux-saga/effects'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import LayoutActions from '../../actions/Layout'
import AUTH_EXPERIENCE_TYPES from '../../types/experiences/Auth'
import ProfileService from '../../../services/Profile'
import AuthExperienceActions from '../../actions/experiences/Auth'
import StoreProfile from '../../models/Profile'
import { normalize } from 'normalizr'
import RESTModelNormalizationSchemas from '../../models/RESTModelNormalizationSchemas'
import DataStoresActions from '../../actions/data'
import StoreImage from '../../models/Image'
import MessageActions from '../../actions/Messages'
import {
  AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT,
  DISPLAYED_MESSAGE_TYPES,
  HTTP_RESPONSE_CODES,
  IS_DEV_ENV
} from '../../../constants/general'
import AuthService from '../../../services/Auth'
import GlobalActions from '../../actions/Global'
import ThemeActions from '../../actions/Theme'
import {
  authorizeHttpClient,
  authorizeHttpClientWithAuth0,
  unAuthorizeHttpClient
} from '../../../clients/mainHTTPClient'
import { connectMainSocket, logoutMainSocket } from '../../../clients/mainSocketClient'
import { isTwoFactorAuthDisabled } from '../../../config/runtimeEnv'
import { getLoginErrorMessage } from '../../../modules/auth/loginErrorMessage'
import { applyProfileLanguage } from '../../../modules/i18n/applyProfileLanguage'
import StoreLanguage from '../../models/Language'
import StoreFamily from '../../models/Family'
import { loadRolesCatalog, loadUserRbacPermissions } from '../AC'

function * mergeProfileFamilyFromNormalizedData (normalizedData, profile) {
  if (!profile?.familyId) {
    return
  }

  const familyEntity = normalizedData.entities[RESTModelNormalizationSchemas.family.entityName]?.[profile.familyId]
  if (!familyEntity) {
    return
  }

  yield put(
    DataStoresActions.mergeEntitiesIntoStores({
      [RESTModelNormalizationSchemas.family.entityName]: {
        [String(profile.familyId)]: StoreFamily.FromNormalizedRESTModel(familyEntity).toJSON()
      }
    })
  )
}

/** Real-time socket only — must not block or undo REST session setup. */
function * connectMainSocketInBackground () {
  try {
    yield call(connectMainSocket)
  } catch (e) {
    if (IS_DEV_ENV) {
      console.warn('[Auth] Real-time socket unavailable; REST CRUD is unaffected.', e?.message || e)
    }
  }
}

function * logIn (action) {
  const fetchId = 'AuthExperienceSaga.logIn'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    yield put(AuthExperienceActions.clearLoginError())

    const { data: { email, password } } = action
    const result = yield AuthService.LogIn(email, password)
    switch (result.status) {
      case HTTP_RESPONSE_CODES.SUCCESS: {
        const data = result.data || {}
        const authToken = data.authToken ?? data.AuthToken
        const authCodePrefix = data.authCodePrefix ?? data.AuthCodePrefix

        // API skipped 2FA (e.g. Auth:DisableTwoFactor) — complete sign-in immediately
        if (authToken) {
          yield put(AuthExperienceActions.clearLoginError())
          yield put(AuthExperienceActions.clearAuthCodePrefix())
          yield call(AuthService.PersistAuthToken, authToken, false)
          yield put(
            AuthExperienceActions.validateAuthToken(undefined, authToken)
          )
          break
        }

        if (authCodePrefix) {
          yield put(AuthExperienceActions.clearLoginError())
          yield put(
            AuthExperienceActions.persistAuthCodePrefix(authCodePrefix)
          )
          yield put(
            AuthExperienceActions.fetchAuthCodePrefix()
          )
          break
        }

        if (isTwoFactorAuthDisabled()) {
          yield put(
            AuthExperienceActions.setLoginError(
              'Sign in could not be completed. Enable Auth:DisableTwoFactor on the API (appsettings.Development.json) or set REACT_APP_DISABLE_2FA=true and use http://localhost:5088.'
            )
          )
        } else {
          yield put(
            AuthExperienceActions.setLoginError(
              'Sign in could not continue. No verification code was issued — check API email settings or try again.'
            )
          )
        }
        break
      }
      case HTTP_RESPONSE_CODES.UNAUTHORIZED: {
        yield put(
          AuthExperienceActions.setLoginError(
            'Invalid email or password. Please check your credentials and try again.'
          )
        )
        break
      }
      default:
        throw new Error('Unsuccessful login')
    }
  } catch (e) {
    yield put(
      AuthExperienceActions.setLoginError(
        getLoginErrorMessage(e, 'Sign in failed. Please try again.')
      )
    )
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->logIn]: Failed to log in user', getLoginErrorMessage(e), e),
        true,
        false,
        false,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * logOut () {
  const fetchId = 'AuthExperienceSaga.logOut'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    yield call(AuthService.LogOut)
    yield call(logoutMainSocket)
    yield put(AuthExperienceActions.removeAuthToken())
    // reset the entire redux store
    yield put(GlobalActions.resetStore())
    // un-authorize the http client because the token it is authorized with became invalid with the logout
    unAuthorizeHttpClient()
    // set authentication checked flag to true, because authentication is considered checked after logout
    yield put(
      AuthExperienceActions.setAuthenticationChecked(true)
    )
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->logOut]: Failed to log out user', 'Logout failed', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * auth0Login () {
  const fetchId = 'AuthExperienceSaga.auth0Login'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    // run auth0 authentication
    yield AuthService.Auth0Login()
    // check authentication state after auth0 authentication has been finished
    yield put(AuthExperienceActions.verifyAuthentication())
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[AuthExperienceSaga->auth0Login]: Failed to redirect user to the auth0 login page',
          'Failed to authenticate with WSS',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * auth0Logout () {
  const fetchId = 'AuthExperienceSaga.auth0Logout'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    // run auth0 logout
    yield AuthService.Auth0Logout()
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[AuthExperienceSaga->auth0Logout]: Failed to log out user with auth0',
          'Failed to log out',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * verifyAuthentication () {
  const fetchId = 'AuthExperienceSaga.verifyAuthentication'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    // validate using the locally stored auth token (from standard login)
    yield put(
      AuthExperienceActions.validateAuthToken()
    )
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[AuthExperienceSaga->verifyAuthentication]: Failed to verify authentication',
          'Failed to verify an existing authentication',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * validateAuth0Login () {
  const fetchId = 'AuthExperienceSaga.validateAuth0Login'

  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    // authorize http client to use auth0 auth mechanism, so it can be used for validating the token with ProfileService.GetProfileInfo
    // - clear any previous authorizations
    unAuthorizeHttpClient()
    // - authorize client
    authorizeHttpClientWithAuth0()
    // check login validity on the server by fetching the profile info of the logged-in user
    const result = yield ProfileService.GetProfileInfo()
    // if profile info is successfully fetched
    if (result.status === HTTP_RESPONSE_CODES.SUCCESS) {
      const normalizedData = normalize(result.data, RESTModelNormalizationSchemas.profile.schema)
      const profile = StoreProfile.FromNormalizedRESTModel(
        normalizedData.entities[RESTModelNormalizationSchemas.profile.entityName][normalizedData.result]
      )
      if (profile.photoId) {
        yield put(
          DataStoresActions.mergeEntitiesIntoStores({
            [RESTModelNormalizationSchemas.image.entityName]: StoreImage.FromNormalizedRESTModel(
              normalizedData.entities[RESTModelNormalizationSchemas.image.entityName][profile.photoId]
            ).toJSON()
          })
        )
      }
      yield call(mergeProfileFamilyFromNormalizedData, normalizedData, profile)
      if (profile.theme) { // change current theme to the theme selected by the user
        yield put(
          ThemeActions.selectTheme(profile.theme)
        )
      }
      // store profile info in the store
      yield put(
        AuthExperienceActions.setProfile(
          profile.toJSON()
        )
      )
      yield call(loadRolesCatalog)
      yield call(loadUserRbacPermissions, profile.toJSON())
      // token is valid so the user is authenticated
      yield put(
        AuthExperienceActions.setIsAuthenticated(true)
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[AuthExperienceSaga->validateAuth0Login]: Failed to validate auth0 login',
          'Authentication failed',
          e
        ),
        true,
        false,
        false,
        false
      )
    )
  }
  // set authentication checked flag, because authentication check has been finished
  yield put(
    AuthExperienceActions.setAuthenticationChecked(true)
  )
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * validateAuthCode (action) {
  const fetchId = 'AuthExperienceSaga.validateAuthCode'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    yield put(AuthExperienceActions.clearLoginError())

    const { data: { authCode, rememberLogin, onLoginSuccess } } = action
    const result = yield AuthService.ValidateAuthCode(authCode)
    switch (result.status) {
      case HTTP_RESPONSE_CODES.SUCCESS: {
        const { authToken } = result.data
        yield put(AuthExperienceActions.clearLoginError())
        yield put(
          AuthExperienceActions.clearAuthCodePrefix()
        )
        yield call(AuthService.PersistAuthToken, authToken, rememberLogin)
        yield put(
          AuthExperienceActions.validateAuthToken(onLoginSuccess, authToken)
        )
        break
      }
      case HTTP_RESPONSE_CODES.UNAUTHORIZED: {
        const { authCodePrefix } = result.data
        yield put(
          AuthExperienceActions.persistAuthCodePrefix(authCodePrefix)
        )
        yield put(
          AuthExperienceActions.fetchAuthCodePrefix()
        )
        yield put(
          AuthExperienceActions.setLoginError(
            'Invalid verification code. A new code has been sent to your email.'
          )
        )
        break
      }
      case HTTP_RESPONSE_CODES.FORBIDDEN: {
        yield put(
          AuthExperienceActions.clearAuthCodePrefix()
        )
        yield put(
          AuthExperienceActions.setLoginError(
            'Verification expired or blocked. Please sign in again with your email and password.'
          )
        )
        break
      }
      default:
        throw new Error('Unsuccessful auth code validation')
    }
  } catch (e) {
    yield put(
      AuthExperienceActions.setLoginError(
        getLoginErrorMessage(e, 'Verification failed. Please try again.')
      )
    )
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->validateAuthCode]: Failed to validate auth code', getLoginErrorMessage(e), e),
        true,
        false,
        false,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * persistAuthToken (action) {
  const fetchId = 'AuthExperienceSaga.persistAuthToken'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { authToken, rememberToken } } = action
    yield AuthService.PersistAuthToken(authToken, rememberToken)
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->persistAuthToken]: Failed to persist the logged-in user\'s auth token', '', e),
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * validateAuthToken (action) {
  const fetchId = 'AuthExperienceSaga.validateAuthToken'
  const onValidationSuccess = action?.data?.onValidationSuccess
  const authTokenFromAction = action?.data?.authToken

  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    // Prefer token from login action; otherwise read storage (e.g. page refresh)
    const persistedToken = authTokenFromAction || AuthService.GetAuthToken()
    if (persistedToken) {
      unAuthorizeHttpClient()
      authorizeHttpClient(persistedToken)
      const result = yield ProfileService.GetProfileInfo()
      if (result.status === HTTP_RESPONSE_CODES.SUCCESS) {
        const normalizedData = normalize(result.data, RESTModelNormalizationSchemas.profile.schema)
        const profile = StoreProfile.FromNormalizedRESTModel(
          normalizedData.entities[RESTModelNormalizationSchemas.profile.entityName][normalizedData.result]
        )
        if (profile.photoId) {
          yield put(
            DataStoresActions.mergeEntitiesIntoStores({
              [RESTModelNormalizationSchemas.image.entityName]: StoreImage.FromNormalizedRESTModel(
                normalizedData.entities[RESTModelNormalizationSchemas.image.entityName][profile.photoId]
              ).toJSON()
            })
          )
        }
        if (profile.languageId) {
          const languageEntity = normalizedData.entities[RESTModelNormalizationSchemas.language.entityName]?.[profile.languageId]
          if (languageEntity) {
            yield put(
              DataStoresActions.mergeEntitiesIntoStores({
                [RESTModelNormalizationSchemas.language.entityName]: {
                  [String(profile.languageId)]: StoreLanguage.FromNormalizedRESTModel(languageEntity).toJSON()
                }
              })
            )
          }
        }
        yield call(mergeProfileFamilyFromNormalizedData, normalizedData, profile)
        if (profile.theme) {
          yield put(
            ThemeActions.selectTheme(profile.theme)
          )
        }
        yield call(applyProfileLanguage, {
          languageId: profile.languageId,
          languageCode: result.data?.language?.code,
          languageName: result.data?.language?.name
        })
        yield put(
          AuthExperienceActions.setProfile(
            profile.toJSON()
          )
        )
        yield call(loadRolesCatalog)
        yield call(loadUserRbacPermissions, profile.toJSON())
        yield put(
          AuthExperienceActions.setIsAuthenticated(true)
        )
        onValidationSuccess && onValidationSuccess()
        // Real-time channel (chat/map) — parallel to REST; failures must not block CRUD.
        yield fork(connectMainSocketInBackground)
      } else {
        yield put(AuthExperienceActions.removeAuthToken())
      }
    }
  } catch (e) {
    const profileMessage = e?.response?.status === 503 && e?.response?.data?.message
      ? e.response.data.message
      : getLoginErrorMessage(e, 'Could not load your profile after sign in. Please try again.')
    yield put(AuthExperienceActions.setLoginError(profileMessage))
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->validateAuthToken]: Failed to validate auth token', profileMessage, e),
        true,
        false,
        false,
        false
      )
    )
    yield put(AuthExperienceActions.removeAuthToken())
  }
  yield put(
    AuthExperienceActions.setAuthenticationChecked(true)
  )
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * removeAuthToken () {
  const fetchId = 'AuthExperienceSaga.removeAuthToken'

  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    // remove persisted auth token
    yield AuthService.RemoveAuthToken()
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->removeAuthToken]: Failed to remove auth token', '', e),
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchAuthCodePrefix (action) {
  const fetchId = 'AuthExperienceSaga.fetchAuthCodePrefix'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const result = yield AuthService.GetAuthCodePrefix()
    if (result) {
      yield put(AuthExperienceActions.setAuthCodePrefix(result))
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->fetchAuthCodePrefix]: Failed to fetch auth code prefix', 'Failed to fetch auth code prefix', e),
        false,
        false,
        false,
        false
      )
    )
  }
  yield put(AuthExperienceActions.setAuthCodePrefixFetched(true))
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * persistAuthCodePrefix (action) {
  const fetchId = 'AuthExperienceSaga.persistAuthCodePrefix'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { authCodePrefix } } = action
    yield AuthService.PersistAuthCodePrefix(authCodePrefix)
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->persistAuthCodePrefix]: Failed to persist the auth code prefix', '', e),
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * clearAuthCodePrefix () {
  const fetchId = 'AuthExperienceSaga.clearAuthCodePrefix'

  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))
    // remove persisted auth code prefix
    yield AuthService.RemoveAuthCodePrefix()
    yield put(AuthExperienceActions.setAuthCodePrefix(null))
    // reset auth code prefix fetched state
    yield put(AuthExperienceActions.setAuthCodePrefixFetched(false))
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->clearAuthCodePrefix]: Failed to clear auth code prefix', '', e),
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * editProfile (action) {
  const fetchId = 'AuthExperienceSaga.editProfile'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { profile, onSuccess } } = action

    const result = yield ProfileService.Update({
      ...profile.toNormalizedRESTModel(),
      browserNotification: profile.browserNotification
    })
    if (result.status === 200) {
      const normalizedData = normalize(result.data, RESTModelNormalizationSchemas.profile.schema)
      const updatedProfile = StoreProfile.FromNormalizedRESTModel(
        normalizedData.entities[RESTModelNormalizationSchemas.profile.entityName][normalizedData.result]
      )
      if (updatedProfile.photoId) {
        yield put(
          DataStoresActions.mergeEntitiesIntoStores({
            [RESTModelNormalizationSchemas.image.entityName]: StoreImage.FromNormalizedRESTModel(
              normalizedData.entities[RESTModelNormalizationSchemas.image.entityName][updatedProfile.photoId]
            ).toJSON()
          })
        )
      }
      if (updatedProfile.languageId) {
        const languageEntity = normalizedData.entities[RESTModelNormalizationSchemas.language.entityName]?.[updatedProfile.languageId]
        if (languageEntity) {
          yield put(
            DataStoresActions.mergeEntitiesIntoStores({
              [RESTModelNormalizationSchemas.language.entityName]: {
                [String(updatedProfile.languageId)]: StoreLanguage.FromNormalizedRESTModel(languageEntity).toJSON()
              }
            })
          )
        }
      }
      yield call(applyProfileLanguage, {
        languageId: updatedProfile.languageId,
        languageCode: result.data?.language?.code,
        languageName: result.data?.language?.name
      })
      yield put(
        AuthExperienceActions.setProfile({
          ...updatedProfile.toJSON(),
          browserNotification: profile.browserNotification
        })
      )
      if (updatedProfile.theme) {
        yield put(
          ThemeActions.selectTheme(updatedProfile.theme)
        )
      }
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_PROFILE_SUCCESS', 'Profile successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->editProfile]: Failed to update profile', 'Failed to update profile', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * changeProfilePassword (action) {
  const fetchId = 'AuthExperienceSaga.changeProfilePassword'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { password, onSuccess } } = action

    const result = yield ProfileService.ChangePassword(password)
    if (result.status === 200) {
      const normalizedData = normalize(result.data, RESTModelNormalizationSchemas.profile.schema)
      const updatedProfile = StoreProfile.FromNormalizedRESTModel(
        normalizedData.entities[RESTModelNormalizationSchemas.profile.entityName][normalizedData.result]
      )
      const currentProfile = yield select(state => state.experiences.auth.profile)
      yield put(
        AuthExperienceActions.setProfile({
          ...updatedProfile.toJSON(),
          hasPassword: true,
          browserNotification: currentProfile?.browserNotification
        })
      )
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['CHANGE_PASSWORD_SUCCESS', 'Password successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[AuthExperienceSaga->changeProfilePassword]: Failed to change password', 'Failed to change password', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * AuthExperienceSaga () {
  yield takeLatest(AUTH_EXPERIENCE_TYPES.LOG_IN, logIn)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.LOG_OUT, logOut)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.AUTH0_LOGIN, auth0Login)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.AUTH0_LOGOUT, auth0Logout)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.VALIDATE_AUTH0_LOGIN, validateAuth0Login)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.VERIFY_AUTHENTICATION, verifyAuthentication)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.VALIDATE_AUTH_CODE, validateAuthCode)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.PERSIST_AUTH_TOKEN, persistAuthToken)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.VALIDATE_AUTH_TOKEN, validateAuthToken)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.REMOVE_AUTH_TOKEN, removeAuthToken)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.FETCH_AUTH_CODE_PREFIX, fetchAuthCodePrefix)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.PERSIST_AUTH_CODE_PREFIX, persistAuthCodePrefix)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.CLEAR_AUTH_CODE_PREFIX, clearAuthCodePrefix)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.EDIT_PROFIlE, editProfile)
  yield takeLatest(AUTH_EXPERIENCE_TYPES.CHANGE_PROFILE_PASSWORD, changeProfilePassword)
}

export default AuthExperienceSaga
